import { Text, Icon, IconType } from '@/ui';
import styles from '../styles.module.css';
import { useTranslation } from 'react-i18next';
import { Column, Header, Row } from '@/components/SettingsLayout';
import { CustomSelect } from '@/components';
import { SelectOption } from '@/types/types';
import { useMemo, useState } from 'react';
import PieChart from './PieChart';
import Legend from './Legend';
import { TimePeriod, TopOrganization } from '@/services/Dashboard/dashboard.service';
import { CompanyTypes } from '@/types';
import useFormatNumber from '@/hooks/useFormatNumber';
import { formatCurrency } from '@/utils/currency';
import { CurrencyCode } from '@final-commerce/common';

export const MAX_TOP_ITEMS = 5;

export const dateRanges: SelectOption[] = [
  { label: 'Today', value: TimePeriod.TODAY },
  { label: '7 days', value: TimePeriod.THIS_WEEK },
  { label: 'This month', value: TimePeriod.THIS_MONTH },
  { label: 'This Year', value: TimePeriod.THIS_YEAR },
  { label: 'All time', value: TimePeriod.ALL_TIME },
];

type Props<T extends Record<string, any>> = {
  title: string;
  icon?: IconType;
  data: TopOrganization[] | undefined;
  dateRangeKey?: keyof T;
  sortKey?: keyof T;
  onFilterChange: (key: keyof T, value: any) => void;
  entityType: CompanyTypes;
};

function TopOrganizations<T extends Record<string, any>>({
  title,
  data,
  onFilterChange,
  dateRangeKey,
  sortKey,
  entityType,
}: Props<T>) {
  const { t } = useTranslation();
  const formatNumber = useFormatNumber();
  const [range, setRange] = useState(dateRanges.find((r) => r.value === TimePeriod.ALL_TIME));

  const topItems = useMemo(() => {
    if (!data) return undefined;
    return data.slice(0, MAX_TOP_ITEMS);
  }, [data]);

  const formatOuterValue = (value: number, index: number) => {
    const currency = topItems?.[index]?.currency;
    return currency
      ? formatCurrency(value, currency as CurrencyCode)
      : formatNumber(value, false, undefined, true);
  };

  const formatInnerValue = (value: number) => formatNumber(value, false).toString();

  const handleDateRangeSelect = (value: SelectOption) => {
    setRange(value);
    if (dateRangeKey) {
      onFilterChange(dateRangeKey, value.value);
    }
  };

  const handleSortChange = (value: string) => {
    if (sortKey) {
      onFilterChange(sortKey, value);
    }
  };

  const transactionCounts = useMemo(() => {
    if (!topItems) return undefined;
    return topItems
      .map((item) => item.totalTransaction)
      .filter((val): val is number => val !== undefined && val !== null);
  }, [topItems]);

  const paymentVolumes = useMemo(() => {
    if (!topItems) return undefined;
    return topItems
      .map((item) => item.totalPaymentVolume)
      .filter((val): val is number => val !== undefined && val !== null);
  }, [topItems]);

  return (
    <div className={styles.orgDiv}>
      <Column gapSize='48' fullWidth>
        <Header>
          <Row>
            <Text size='l' weight='medium'>
              {t(title)}
            </Text>
          </Row>
          <Row gapSize='8' fullWidth={false}>
            <div className={styles.selectContainer}>
              <CustomSelect
                icon={true}
                options={dateRanges}
                defValue={range}
                onChange={handleDateRangeSelect}
                showDropdownIndicator={false}
                testId='date-range-select'
              />
            </div>
            <Icon type={IconType.SortBy} fill='transparent' width={24} height={24} />
          </Row>
        </Header>
        <div className={styles.chartAndLegendContainer}>
          <div className={styles.chartHalf}>
            <PieChart
              outerData={paymentVolumes}
              innerData={transactionCounts}
              topItems={topItems}
              formatOuterValue={formatOuterValue}
              formatInnerValue={formatInnerValue}
            />
          </div>
          <div className={styles.legendHalf}>
            <Legend data={topItems || []} onSortChange={handleSortChange} entityType={entityType} />
          </div>
        </div>
      </Column>
    </div>
  );
}

export default TopOrganizations;
